import React from "react";
import { View, Text, StyleSheet, Pressable, Platform } from "react-native";
import Svg, { Path } from "react-native-svg";
import NtMarketingPageContainer from "../../marketing/components/general/NtMarketingPageContainer";
import { useTheme } from "../../context/ThemeContext";
import { useMobile } from "../../hooks/useMobile";
import { useNavigation, useRoute } from "@react-navigation/native";
import routes from "../../navigation/routes";
import {
  Reveal,
  LiveDot,
  FONT_DISPLAY,
  FONT_MONO,
} from "../../marketing/components/voxcraft/_shared";

// =============================================================================
// 404 — styled to match the Voxcraft marketing surface. Wraps in the marketing
// container so the sticky nav + footer come along for navigation/recovery.
// =============================================================================

function NotFoundView() {
  const { theme } = useTheme();
  const nav = useNavigation();
  const route = useRoute();
  const isMobile = useMobile();

  // RN's route object exposes `path` on web (the unmatched URL). On native
  // there is no URL to show; fall back to the route name.
  const requestedPath =
    (Platform.OS === "web" && route?.path) || route?.name || "/";

  return (
    <View
      style={[styles.outer, { paddingVertical: isMobile ? 80 : 120 }]}
    >
      <Reveal style={[styles.card, { borderColor: theme.borderSoft, backgroundColor: theme.bg2 }]}>
        {/* HUD-style line, mirroring the hero's NETWORK · OPERATIONAL */}
        <View style={styles.hud}>
          <LiveDot color={theme.red || "#e05b59"} style={{ marginRight: 7 }} />
          <Text style={[styles.hudText, { color: theme.textMute }]}>
            ERROR · 404 · ROUTE NOT FOUND
          </Text>
        </View>

        {/* Big 404 display number */}
        <Text style={[styles.bigCode, { color: theme.brand2 }]}>404</Text>

        <Text style={[styles.title, { color: theme.text }]}>
          We can't find that page.
        </Text>
        <Text style={[styles.titleSoft, { color: theme.textDim }]}>
          The line you dialed isn't in service.
        </Text>

        <Text style={[styles.lede, { color: theme.textDim }]}>
          The page you requested doesn't exist, has moved, or you typed
          the URL by hand and a digit's off. Head back to the main site
          or sign in to your tenant. Both routes are below.
        </Text>

        {/* Mono pill showing the unresolved path */}
        <View
          style={[
            styles.pathPill,
            { borderColor: theme.borderSoft, backgroundColor: theme.surface },
          ]}
        >
          <Text style={[styles.pathPillLabel, { color: theme.textMute }]}>
            REQUESTED
          </Text>
          <Text style={[styles.pathPillValue, { color: theme.text }]} numberOfLines={1}>
            {requestedPath}
          </Text>
        </View>

        <View style={styles.ctaRow}>
          <Pressable
            onPress={() => nav.navigate(routes.marketing)}
            style={({ pressed }) => [
              styles.btn,
              styles.btnPrimary,
              { backgroundColor: theme.brand2 },
              pressed && styles.btnPressed,
            ]}
          >
            <Svg width={14} height={14} viewBox="0 0 24 24" style={{ marginRight: 8 }}>
              <Path
                stroke="#fff"
                strokeWidth={2}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 12l9-9 9 9M5 10v10h14V10"
              />
            </Svg>
            <Text style={styles.btnPrimaryText}>Back to home</Text>
          </Pressable>

          <Pressable
            onPress={() => nav.navigate(routes.auth)}
            style={({ pressed }) => [
              styles.btn,
              styles.btnGhost,
              { borderColor: theme.borderSoft, backgroundColor: theme.surface },
              pressed && styles.btnPressed,
            ]}
          >
            <Text style={[styles.btnGhostText, { color: theme.text }]}>
              Sign in →
            </Text>
          </Pressable>
        </View>

        {/* Helpful pointers — matches the AI-section checklist treatment */}
        <View style={[styles.tips, { borderTopColor: theme.borderSoft }]}>
          <Text style={[styles.tipsLabel, { color: theme.textMute }]}>
            TRY ONE OF THESE
          </Text>
          <View style={styles.tipRow}>
            <Pressable onPress={() => nav.navigate(routes.marketing)}>
              <Text style={[styles.tipLink, { color: theme.brand2 }]}>
                voxcraft.ai
              </Text>
            </Pressable>
            <Text style={[styles.tipSep, { color: theme.textMute }]}>·</Text>
            <Pressable onPress={() => nav.navigate(routes.onboarding)}>
              <Text style={[styles.tipLink, { color: theme.brand2 }]}>
                Try the AI setup wizard
              </Text>
            </Pressable>
            <Text style={[styles.tipSep, { color: theme.textMute }]}>·</Text>
            <Pressable onPress={() => nav.navigate(routes.learnmore)}>
              <Text style={[styles.tipLink, { color: theme.brand2 }]}>
                Learn more
              </Text>
            </Pressable>
          </View>
        </View>
      </Reveal>
    </View>
  );
}

// Navigator passes `navigation` + `route` props. Wrap the functional view so
// it can still resolve those via the hooks-based useNavigation/useRoute —
// works the same for both class and functional component registrations.
function NotFoundController() {
  return (
    <NtMarketingPageContainer>
      <NotFoundView />
    </NtMarketingPageContainer>
  );
}

const styles = StyleSheet.create({
  outer: { paddingHorizontal: 28, alignItems: "center" },
  card: {
    width: "100%", maxWidth: 720,
    paddingHorizontal: 40, paddingVertical: 56,
    borderRadius: 16, borderWidth: 1,
    alignItems: "flex-start",
  },

  hud: { flexDirection: "row", alignItems: "center", marginBottom: 28 },
  hudText: { fontFamily: FONT_MONO, fontSize: 11, letterSpacing: 1.4, textTransform: "uppercase" },

  bigCode: {
    fontFamily: FONT_DISPLAY, fontSize: 96, fontWeight: "800",
    letterSpacing: -4, lineHeight: 96, marginBottom: 4,
  },

  title:     { fontFamily: FONT_DISPLAY, fontSize: 36, fontWeight: "700", letterSpacing: -1, lineHeight: 40 },
  titleSoft: { fontFamily: FONT_DISPLAY, fontSize: 36, fontWeight: "500", letterSpacing: -1, lineHeight: 40, marginBottom: 16 },
  lede:      { fontSize: 15, lineHeight: 24, marginBottom: 24, maxWidth: 560 },

  pathPill: {
    flexDirection: "row", alignItems: "center",
    paddingHorizontal: 12, paddingVertical: 8,
    borderRadius: 8, borderWidth: 1,
    marginBottom: 28, maxWidth: "100%",
  },
  pathPillLabel: {
    fontFamily: FONT_MONO, fontSize: 10, letterSpacing: 1.2,
    marginRight: 10, textTransform: "uppercase",
  },
  pathPillValue: { fontFamily: FONT_MONO, fontSize: 12, flexShrink: 1 },

  ctaRow: { flexDirection: "row", flexWrap: "wrap", gap: 10, marginBottom: 32 },
  btn: {
    flexDirection: "row", alignItems: "center", justifyContent: "center",
    paddingHorizontal: 22, paddingVertical: 12,
    borderRadius: 8, borderWidth: 1, borderColor: "transparent",
  },
  btnPrimary: {
    shadowColor: "#2570BD", shadowOpacity: 0.35, shadowRadius: 16, shadowOffset: { width: 0, height: 6 },
  },
  btnPrimaryText: { color: "#fff", fontSize: 14, fontWeight: "600" },
  btnGhost: {},
  btnGhostText: { fontSize: 14, fontWeight: "600" },
  btnPressed: { opacity: 0.85, transform: [{ translateY: -1 }] },

  tips: { borderTopWidth: 1, paddingTop: 18, width: "100%" },
  tipsLabel: { fontFamily: FONT_MONO, fontSize: 10, letterSpacing: 1.2, marginBottom: 10, textTransform: "uppercase" },
  tipRow: { flexDirection: "row", alignItems: "center", flexWrap: "wrap" },
  tipLink: { fontSize: 13, fontWeight: "600", paddingVertical: 4 },
  tipSep:  { fontSize: 13, paddingHorizontal: 10 },
});

export default NotFoundController;
